import { ArrowDown, ArrowUp, ImageOff, RefreshCw, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import rootStore from "../../stores/rootStore";
import InlineSpinner from "../util/InlineSpinner";
import {
  deleteManufacturerProductImage,
  getManufacturerProductImages,
  reorderManufacturerProductImages,
  setManufacturerProductImagePrimary,
  uploadManufacturerProductImages,
} from "./productImagesApi";

type ProductImageItem = {
  id: string;
  url: string | null;
  isPrimary: boolean;
  sortOrder: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readString = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return null;
};

const readBoolean = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "boolean") return value;
  }
  return null;
};

const readNumber = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
};

const normalizeImagesResponse = (data: unknown): ProductImageItem[] => {
  const rawList: unknown[] = (() => {
    if (Array.isArray(data)) return data;
    if (isRecord(data)) {
      const maybeImages = (data.Images ?? data.images) as unknown;
      if (Array.isArray(maybeImages)) return maybeImages;
    }
    return [];
  })();

  const mapped = rawList
    .map((raw, index) => {
      if (!isRecord(raw)) return null;
      const id = readString(raw, ["id", "Id", "imageId", "ImageId", "productImageId", "ProductImageId"]);
      if (!id) return null;
      const url = readString(raw, [
        "url",
        "Url",
        "uri",
        "Uri",
        "fileUri",
        "FileUri",
        "imageUrl",
        "ImageUrl",
        "src",
        "Src",
      ]);
      const sortOrder = readNumber(raw, ["sortOrder", "SortOrder", "order", "Order", "position", "Position", "index", "Index"]) ?? index;
      const isPrimary = readBoolean(raw, ["isPrimary", "IsPrimary", "primary", "Primary"]) ?? false;
      return { id, url, sortOrder, isPrimary };
    })
    .filter((x): x is ProductImageItem => Boolean(x));

  const sorted = mapped.sort((a, b) => a.sortOrder - b.sortOrder);
  const hasPrimary = sorted.some((x) => x.isPrimary);
  if (!hasPrimary && sorted.length > 0) {
    sorted[0] = { ...sorted[0], isPrimary: true };
  }

  return sorted;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  randmarSKU?: string;
}

export default function ManageProductImagesDialog({ open, onOpenChange, randmarSKU }: Props) {
  const { applicationId } = useManufacturer();
  const { toast } = useToast();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const accessToken = rootStore.userStore.user?.access_token;

  const [images, setImages] = useState<ProductImageItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [mutatingImageId, setMutatingImageId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const isBusy = isLoading || isUploading || isReordering || Boolean(mutatingImageId);

  const canFetch = Boolean(open && applicationId && randmarSKU);

  const primaryImageId = useMemo(() => images[0]?.id ?? null, [images]);

  const refresh = useCallback(async () => {
    if (!applicationId || !randmarSKU) return;
    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    setIsLoading(true);
    setErrorMessage(null);
    try {
      const data = await getManufacturerProductImages({
        manufacturerId: applicationId,
        sku: randmarSKU,
        accessToken,
        signal: abortController.signal,
      });
      setImages(normalizeImagesResponse(data));
      setLastRefreshedAt(new Date());
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load product images.";
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, applicationId, randmarSKU]);

  useEffect(() => {
    if (open) {
      void refresh();
      return;
    }
    abortRef.current?.abort();
    abortRef.current = null;
    setErrorMessage(null);
  }, [open, refresh]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleUploadFiles = useCallback(
    async (files: File[]) => {
      if (!randmarSKU) {
        toast({ variant: "destructive", title: "Missing SKU", description: "Cannot upload images without a valid SKU." });
        return;
      }
      if (!applicationId) {
        toast({
          variant: "destructive",
          title: "Missing manufacturer",
          description: "Unable to determine the current manufacturer. Please reload and try again.",
        });
        return;
      }
      if (!files.length) {
        toast({ variant: "destructive", title: "No files selected", description: "Select one or more images to upload." });
        return;
      }

      setIsUploading(true);
      try {
        await uploadManufacturerProductImages({ manufacturerId: applicationId, sku: randmarSKU, files, accessToken });
        toast({
          variant: "success",
          title: "Uploaded",
          description: files.length === 1 ? "Image uploaded successfully." : `${files.length} images uploaded successfully.`,
        });
        await refresh();
      } catch (error) {
        const message = error instanceof Error ? error.message : "There was a problem uploading your images. Please try again.";
        toast({ variant: "destructive", title: "Upload failed", description: message });
      } finally {
        setIsUploading(false);
      }
    },
    [accessToken, applicationId, randmarSKU, refresh, toast],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(event.target.files ?? []);
      event.target.value = "";
      await handleUploadFiles(selected);
    },
    [handleUploadFiles],
  );

  const handleSetPrimary = useCallback(
    async (imageId: string) => {
      if (!applicationId || !randmarSKU) return;
      setMutatingImageId(imageId);
      try {
        await setManufacturerProductImagePrimary({ manufacturerId: applicationId, sku: randmarSKU, imageId, accessToken });
        toast({ variant: "success", title: "Updated", description: "Primary image updated." });
        await refresh();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to set primary image.";
        toast({ variant: "destructive", title: "Error", description: message });
      } finally {
        setMutatingImageId(null);
      }
    },
    [accessToken, applicationId, randmarSKU, refresh, toast],
  );

  const handleReorder = useCallback(
    async (newImages: ProductImageItem[]) => {
      if (!applicationId || !randmarSKU) return;
      const idsInOrder = newImages.map((x) => x.id);

      setIsReordering(true);
      setImages(newImages);
      try {
        await reorderManufacturerProductImages({
          manufacturerId: applicationId,
          sku: randmarSKU,
          imageIdsInOrder: idsInOrder,
          accessToken,
        });
        toast({ variant: "success", title: "Updated", description: "Image order updated." });
        await refresh();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to reorder images.";
        toast({ variant: "destructive", title: "Error", description: message });
        await refresh();
      } finally {
        setIsReordering(false);
      }
    },
    [accessToken, applicationId, randmarSKU, refresh, toast],
  );

  const moveImage = useCallback(
    async (index: number, direction: "up" | "down") => {
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (index < 0 || index >= images.length) return;
      if (nextIndex < 0 || nextIndex >= images.length) return;
      const newImages = [...images];
      const tmp = newImages[index];
      newImages[index] = newImages[nextIndex];
      newImages[nextIndex] = tmp;
      await handleReorder(newImages);
    },
    [handleReorder, images],
  );

  const handleDelete = useCallback(
    async (imageId: string) => {
      if (!applicationId || !randmarSKU) return;
      setMutatingImageId(imageId);
      try {
        await deleteManufacturerProductImage({ manufacturerId: applicationId, sku: randmarSKU, imageId, accessToken });
        toast({ variant: "success", title: "Deleted", description: "Image deleted." });
        await refresh();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to delete image.";
        toast({ variant: "destructive", title: "Error", description: message });
      } finally {
        setMutatingImageId(null);
      }
    },
    [accessToken, applicationId, randmarSKU, refresh, toast],
  );

  const headerRight = useMemo(() => {
    if (!open) return null;
    const lastRefreshedText = lastRefreshedAt
      ? lastRefreshedAt.toLocaleString()
      : isLoading
        ? "Refreshing…"
        : "Not yet refreshed";

    return (
      <div className="flex items-center gap-2">
        <div className="text-xs text-muted-foreground" aria-label={`Last refreshed ${lastRefreshedText}`}>
          {lastRefreshedText}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refresh()}
          disabled={!canFetch || isBusy}
          title="Refresh"
        >
          {isLoading ? <InlineSpinner className="mr-2" decorative /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>
    );
  }, [canFetch, isBusy, isLoading, lastRefreshedAt, open, refresh]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isBusy) {
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>Product images</DialogTitle>
              <DialogDescription>
                Images appear to buyers in this order; first is primary.
              </DialogDescription>
            </div>
            {headerRight}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-base">Upload</CardTitle>
              <CardDescription>Select one or more images to add to this SKU.</CardDescription>
            </CardHeader>
            <CardContent>
              <input
                id={fileInputId}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
                disabled={isBusy}
                ref={fileInputRef}
              />
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  {randmarSKU ? (
                    <span>
                      SKU: <span className="font-medium text-foreground">{randmarSKU}</span>
                    </span>
                  ) : (
                    <span className="text-destructive">Missing SKU</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!randmarSKU || !applicationId || isBusy}
                    title="Upload images"
                  >
                    {isUploading ? <InlineSpinner className="mr-2" decorative /> : null}
                    Upload images
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div>
            {errorMessage ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {errorMessage}
              </div>
            ) : null}

            {isLoading && images.length === 0 ? (
              <div className="flex items-center justify-center rounded-2xl border border-gray-100 bg-white px-6 py-10">
                <InlineSpinner className="mr-2" decorative />
                <div className="text-sm text-muted-foreground">Loading images…</div>
              </div>
            ) : images.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-gray-100 bg-white px-6 py-12 text-center">
                <ImageOff className="h-10 w-10 text-muted-foreground" />
                <div className="text-sm font-medium text-foreground">No images yet</div>
                <div className="text-sm text-muted-foreground">Upload images to show them to buyers.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {images.map((image, index) => {
                  const isPrimary = image.id === primaryImageId;
                  const isRowBusy = isBusy && (mutatingImageId === image.id);

                  return (
                    <Card key={image.id} className="overflow-hidden">
                      <div className="relative aspect-square bg-gray-50">
                        {image.url ? (
                          <img
                            src={image.url}
                            alt={`${randmarSKU ?? "SKU"} image ${index + 1}`}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <ImageOff className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        {isPrimary ? (
                          <Badge variant="randmarPurple" className="absolute left-2 top-2">
                            Primary
                          </Badge>
                        ) : null}
                        <div className="absolute right-2 top-2 flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => void moveImage(index, "up")}
                            disabled={isBusy || index === 0}
                            aria-label="Move image up"
                            title="Move up"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => void moveImage(index, "down")}
                            disabled={isBusy || index === images.length - 1}
                            aria-label="Move image down"
                            title="Move down"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <CardContent className="flex items-center justify-between gap-2 p-3">
                        <div className="text-xs text-muted-foreground">#{index + 1}</div>
                        <div className="flex items-center gap-2">
                          {!isPrimary ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void handleSetPrimary(image.id)}
                              disabled={isBusy}
                              title="Set primary"
                            >
                              {mutatingImageId === image.id ? <InlineSpinner className="mr-2" decorative /> : null}
                              Set primary
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" disabled title="Primary image">
                              Primary
                            </Button>
                          )}

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Delete image"
                                title="Delete"
                                disabled={isBusy}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete image?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the image from this SKU. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel disabled={isRowBusy}>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => void handleDelete(image.id)}
                                  disabled={isRowBusy}
                                >
                                  {isRowBusy ? <InlineSpinner className="mr-2" decorative /> : null}
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isBusy}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
